import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiImage, FiPlus, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import Layout from '../components/Layout';
import Toast, { ToastType } from '../components/Toast';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import DraggableFeatureList from '../components/DraggableFeatureList';
import { apiService, getMediaUrl } from '../services/api';
import { Feature, Package } from '../types';

const EditPackage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [loadingPackage, setLoadingPackage] = useState(true);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [packageFeatures, setPackageFeatures] = useState<Record<number, any>>({});
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [picturePreview, setPicturePreview] = useState<string>('');
  const [featureRefreshKey, setFeatureRefreshKey] = useState(0);
  
  // Delete confirmation dialog state
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    isOpen: boolean;
    featureId: number | null;
    featureName: string;
  }>({ isOpen: false, featureId: null, featureName: '' });
  
  // Feature management state
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState<Feature | null>(null);
  const [featureFormData, setFeatureFormData] = useState({
    key: '',
    title_en: '',
    title_fr: '',
    value_type: 'boolean' as 'boolean' | 'integer' | 'text',
    is_active: true,
    is_new: false,
  });

  const [formData, setFormData] = useState({
    title: '',
    fr_title: '',
    description: '',
    fr_description: '',
    price_1_month: '',
    price_3_months: '',
    price_1_year: '',
    discount_price_1_month: '',
    discount_price_3_months: '',
    discount_price_1_year: '',
    is_popular: false,
    storage_mo: '',
    total_user: '',
    monthly_limit_transaction_ai: '',
    total_transaction_ai: '',
    max_upload: '0',
    max_jobs: '0',
    is_enabled: true,
    features: [] as string[],
    fr_features: [] as string[],
    discount_validity: '',
    data_retention: '',
  });

  useEffect(() => {
    fetchFeatures();
    if (id) {
      fetchPackage();
    }
  }, [id]);

  const fetchFeatures = async () => {
    try {
      setLoadingFeatures(true);
      const response = await apiService.getFeatures();
      if (response.success) {
        setFeatures(response.data);
      }
    } catch (error) {
      console.error('Error fetching features:', error);
    } finally {
      setLoadingFeatures(false);
    }
  };

  const openFeatureModal = (feature?: Feature) => {
    if (feature) {
      setEditingFeature(feature);
      setFeatureFormData({
        key: feature.key,
        title_en: feature.title_en,
        title_fr: feature.title_fr || '',
        value_type: feature.value_type,
        is_active: feature.is_active,
        is_new: feature.is_new,
      });
    } else {
      setEditingFeature(null);
      setFeatureFormData({
        key: '',
        title_en: '',
        title_fr: '',
        value_type: 'boolean',
        is_active: true,
        is_new: false,
      });
    }
    setShowFeatureModal(true);
  };

  const handleFeatureSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault(); // Prevent form submission from triggering package form
    try {
      if (editingFeature) {
        await apiService.updateFeature(editingFeature.id, featureFormData);
        setToast({ message: 'Feature updated successfully!', type: 'success' });
      } else {
        await apiService.createFeature(featureFormData);
        setToast({ message: 'Feature created successfully!', type: 'success' });
      }
      setShowFeatureModal(false);
      fetchFeatures();
      // Force DraggableFeatureList to refresh
      setFeatureRefreshKey(prev => prev + 1);
    } catch (error: any) {
      console.error('Error saving feature:', error);
      setToast({ message: error.message || 'Failed to save feature.', type: 'error' });
    }
  };

  const handleDeleteFeature = (featureId: number, featureName: string) => {
    setDeleteConfirmDialog({
      isOpen: true,
      featureId,
      featureName,
    });
  };

  const confirmDeleteFeature = async () => {
    if (!deleteConfirmDialog.featureId) return;
    
    try {
      await apiService.deleteFeature(deleteConfirmDialog.featureId);
      setToast({ message: 'Feature deleted successfully!', type: 'success' });
      fetchFeatures();
    } catch (error: any) {
      console.error('Error deleting feature:', error);
      setToast({ message: error.message || 'Failed to delete feature.', type: 'error' });
    } finally {
      setDeleteConfirmDialog({ isOpen: false, featureId: null, featureName: '' });
    }
  };

  const fetchPackage = async () => {
    try {
      setLoadingPackage(true);
      const response = await apiService.getPackage(parseInt(id!));
      if (response.success) {
        const pkg = response.data;
        
        setFormData({
          title: pkg.title || '',
          fr_title: pkg.fr_title || '',
          description: pkg.description || '',
          fr_description: pkg.fr_description || '',
          price_1_month: pkg.price_1_month || pkg.monthly_price || '',
          price_3_months: pkg.price_3_months || '',
          price_1_year: pkg.price_1_year || pkg.yearly_price || '',
          discount_price_1_month: pkg.discount_price_1_month || pkg.monthly_discount_price || '',
          discount_price_3_months: pkg.discount_price_3_months || '',
          discount_price_1_year: pkg.discount_price_1_year || pkg.yearly_discount_price || '',
          is_popular: pkg.is_popular || false,
          storage_mo: pkg.storage_mo?.toString() || '',
          total_user: pkg.total_user?.toString() || '',
          monthly_limit_transaction_ai: pkg.monthly_limit_transaction_ai?.toString() || '',
          total_transaction_ai: pkg.total_transaction_ai?.toString() || '',
          max_upload: pkg.max_upload?.toString() || '0',
          max_jobs: pkg.max_jobs?.toString() || '0',
          is_enabled: pkg.is_enabled !== undefined ? pkg.is_enabled : true,
          features: pkg.features || [],
          fr_features: pkg.fr_features || [],
          discount_validity: pkg.discount_validity || '',
          data_retention: pkg.data_retention?.toString() || '',
        });

        if (pkg.picture) {
          setPicturePreview(getMediaUrl(pkg.picture));
        }

        // Load package features
        if (pkg.package_features && pkg.package_features.length > 0) {
          const featuresMap: Record<number, any> = {};
          pkg.package_features.forEach((pf: any) => {
            featuresMap[pf.feature] = pf.value;
          });
          setPackageFeatures(featuresMap);
        } else {
          try {
            const pfResponse = await apiService.getPackageFeatures(pkg.id);
            if (pfResponse.success && pfResponse.data.length > 0) {
              const featuresMap: Record<number, any> = {};
              pfResponse.data.forEach((pf: any) => {
                featuresMap[pf.feature] = pf.value;
              });
              setPackageFeatures(featuresMap);
            }
          } catch (error) {
            console.error('Error loading package features:', error);
          }
        }
      }
    } catch (error: any) {
      console.error('Error fetching package:', error);
      setToast({ message: 'Failed to load package details.', type: 'error' });
    } finally {
      setLoadingPackage(false);
    }
  };

  const handlePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPictureFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPicturePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      formDataToSend.append('title', formData.title);
      formDataToSend.append('fr_title', formData.fr_title || '');
      formDataToSend.append('description', formData.description);
      formDataToSend.append('fr_description', formData.fr_description || '');
      
      // Old pricing (for backward compatibility)
      formDataToSend.append('monthly_price', formData.price_1_month);
      formDataToSend.append('yearly_price', formData.price_1_year);
      
      // New pricing fields
      if (formData.price_1_month) formDataToSend.append('price_1_month', formData.price_1_month);
      if (formData.price_3_months) formDataToSend.append('price_3_months', formData.price_3_months);
      if (formData.price_1_year) formDataToSend.append('price_1_year', formData.price_1_year);
      if (formData.discount_price_1_month) formDataToSend.append('discount_price_1_month', formData.discount_price_1_month);
      if (formData.discount_price_3_months) formDataToSend.append('discount_price_3_months', formData.discount_price_3_months);
      if (formData.discount_price_1_year) formDataToSend.append('discount_price_1_year', formData.discount_price_1_year);
      formDataToSend.append('is_popular', formData.is_popular.toString());
      
      formDataToSend.append('storage_mo', formData.storage_mo);
      formDataToSend.append('total_user', formData.total_user);
      formDataToSend.append('monthly_limit_transaction_ai', formData.monthly_limit_transaction_ai);
      formDataToSend.append('total_transaction_ai', formData.total_transaction_ai);
      formDataToSend.append('max_upload', formData.max_upload || '0');
      formDataToSend.append('max_jobs', formData.max_jobs || '0');
      formDataToSend.append('is_enabled', formData.is_enabled.toString());
      
      if (formData.discount_validity) {
        formDataToSend.append('discount_validity', formData.discount_validity);
      }
      if (formData.data_retention) {
        const retentionValue = parseInt(formData.data_retention, 10);
        if (!isNaN(retentionValue)) {
          formDataToSend.append('data_retention', retentionValue.toString());
        }
      }
      
      formDataToSend.append('features', JSON.stringify(formData.features || []));
      formDataToSend.append('fr_features', JSON.stringify(formData.fr_features || []));
      
      if (pictureFile) {
        formDataToSend.append('picture', pictureFile);
      }

      await apiService.updatePackage(parseInt(id!), formDataToSend);
      
      // Update package features if any were modified
      if (Object.keys(packageFeatures).length > 0) {
        try {
          const featuresToUpdate = Object.entries(packageFeatures).map(([featureId, value]) => ({
            feature_id: parseInt(featureId),
            value: value
          }));
          
          await apiService.bulkUpdatePackageFeatures(parseInt(id!), featuresToUpdate);
        } catch (error) {
          console.error('Error updating package features:', error);
        }
      }

      setToast({ message: 'Package updated successfully!', type: 'success' });
      setTimeout(() => navigate('/settings/packages'), 1500);
    } catch (error: any) {
      console.error('Error updating package:', error);
      setToast({ message: error.message || 'Failed to update package.', type: 'error' });
      setLoading(false);
    }
  };

  if (loadingPackage) {
    return (
      <Layout>
        <div className="flex h-screen items-center justify-center">
          <div className="h-16 w-16 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 dark:bg-navy-900">
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-6 py-4 shadow-sm dark:border-navy-700 dark:bg-navy-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/settings/packages')}
                className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                <FiArrowLeft className="h-5 w-5" />
                Back
              </button>
              <div>
                <h1 className="text-2xl font-bold text-navy-800 dark:text-white">
                  ✏️ Edit Package
                </h1>
              
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2.5 font-medium text-white shadow-lg transition hover:bg-brand-600 disabled:opacity-50"
            >
              <FiSave className="h-5 w-5" />
              {loading ? 'Updating...' : 'Update Package'}
            </button>
          </div>
        </div>

        {/* Form Content - Same as AddPackage but with pre-filled data */}
        <form onSubmit={handleSubmit} className="mx-auto max-w-6xl space-y-6 p-6">
          {/* Basic Information Card */}
          <div className="rounded-xl bg-white p-6 shadow-md dark:bg-navy-800">
            <div className="mb-6 flex items-center gap-2 border-b border-gray-200 pb-3 dark:border-navy-600">
              <span className="text-xl">📝</span>
              <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                Basic Information
              </h2>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  🇬🇧 Package Title (English) *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Professional Plan"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  🇫🇷 Package Title (French)
                  <span className="ml-1 text-xs text-gray-500">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.fr_title}
                  onChange={(e) => setFormData({ ...formData, fr_title: e.target.value })}
                  placeholder="e.g., Plan Professionnel"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  🇬🇧 Description (English) *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the package benefits..."
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  🇫🇷 Description (French)
                  <span className="ml-1 text-xs text-gray-500">(optional)</span>
                </label>
                <textarea
                  value={formData.fr_description}
                  onChange={(e) => setFormData({ ...formData, fr_description: e.target.value })}
                  placeholder="Décrivez les avantages du forfait..."
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
            </div>

            {/* Package Picture */}
            <div className="mt-6">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                <FiImage className="mr-1 inline h-5 w-5" />
                Package Icon/Picture
                <span className="ml-1 text-xs text-gray-500">(optional)</span>
              </label>
              <div className="flex items-center gap-4">
                {picturePreview && (
                  <div className="h-20 w-20 overflow-hidden rounded-lg border-2 border-brand-500">
                    <img src={picturePreview} alt="Preview" className="h-full w-full object-cover" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePictureChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-lg file:border-0 file:bg-brand-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-brand-700 hover:file:bg-brand-100 dark:text-gray-400 dark:file:bg-brand-900/20 dark:file:text-brand-400"
                />
              </div>
              {picturePreview && !pictureFile && (
                <p className="mt-2 text-xs text-gray-500">Current picture shown. Upload a new one to replace it.</p>
              )}
            </div>
          </div>

          {/* Pricing Card - 3-Tier */}
          <div className="rounded-xl bg-white p-6 shadow-md dark:bg-navy-800">
            <div className="mb-6 flex items-center gap-2 border-b border-gray-200 pb-3 dark:border-navy-600">
              <span className="text-xl">💰</span>
              <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                Pricing Plans
              </h2>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {/* 1 Month Plan */}
              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-navy-600 dark:bg-navy-700">
                <div className="mb-4">
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    1 Month
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Price ($) *
                    </label>
                    <input
                      type="text"
                      value={formData.price_1_month}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                          setFormData({ ...formData, price_1_month: val });
                        }
                      }}
                      placeholder="29.99"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Discount ($)
                    </label>
                    <input
                      type="text"
                      value={formData.discount_price_1_month}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                          setFormData({ ...formData, discount_price_1_month: val });
                        }
                      }}
                      placeholder="24.99"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* 3 Months Plan */}
              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-navy-600 dark:bg-navy-700">
                <div className="mb-4">
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    3 Months
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Price ($) *
                    </label>
                    <input
                      type="text"
                      value={formData.price_3_months}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                          setFormData({ ...formData, price_3_months: val });
                        }
                      }}
                      placeholder="79.99"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Discount ($)
                    </label>
                    <input
                      type="text"
                      value={formData.discount_price_3_months}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                          setFormData({ ...formData, discount_price_3_months: val });
                        }
                      }}
                      placeholder="69.99"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* 1 Year Plan */}
              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-navy-600 dark:bg-navy-700">
                <div className="mb-4">
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    1 Year
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Price ($) *
                    </label>
                    <input
                      type="text"
                      value={formData.price_1_year}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                          setFormData({ ...formData, price_1_year: val });
                        }
                      }}
                      placeholder="299.99"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Discount ($)
                    </label>
                    <input
                      type="text"
                      value={formData.discount_price_1_year}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                          setFormData({ ...formData, discount_price_1_year: val });
                        }
                      }}
                      placeholder="249.99"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Package Settings */}
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Popular Badge
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_popular}
                    onChange={(e) => setFormData({ ...formData, is_popular: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Mark as Popular
                  </span>
                </label>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Package Status
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    id="is_enabled_pricing"
                    checked={formData.is_enabled}
                    onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {formData.is_enabled ? 'Active' : 'Disabled'}
                  </span>
                </label>
              </div>
            </div>

            {/* Discount Validity */}
            {(formData.discount_price_1_month || formData.discount_price_3_months || formData.discount_price_1_year) && (
              <div className="mt-4">
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Discount Valid Until (optional)
                </label>
                <input
                  type="date"
                  value={formData.discount_validity}
                  onChange={(e) => setFormData({ ...formData, discount_validity: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                />
              </div>
            )}
          </div>

          {/* Package Features & Limits Card */}
          <div className="rounded-xl bg-white p-6 shadow-md dark:bg-navy-800">
            <div className="mb-6 flex items-center justify-between border-b border-gray-200 pb-3 dark:border-navy-600">
              <div className="flex items-center gap-2">
                <span className="text-xl"></span>
                <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                  Package Features
                </h2>
              </div>
              <button
                type="button"
                onClick={() => openFeatureModal()}
                className="flex items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-green-600"
              >
                <FiPlus className="h-4 w-4" />
                Add Feature
              </button>
            </div>

            {/* Unified Draggable Feature List - All features with inline editing */}
            <DraggableFeatureList
              key={featureRefreshKey}
              packageId={parseInt(id!)}
              packageFeatures={{
                ...packageFeatures,
                // Map static Package fields
                storage_mo: formData.storage_mo,
                total_user: formData.total_user,
                monthly_limit_transaction_ai: formData.monthly_limit_transaction_ai,
                total_transaction_ai: formData.total_transaction_ai,
                max_upload: formData.max_upload,
                max_jobs: formData.max_jobs,
                data_retention: formData.data_retention,
              }}
              onFeaturesChange={(updatedFeatures) => {
                // Update both static and dynamic features
                const newPackageFeatures: Record<number, any> = {};
                const newFormData = { ...formData };
                
                updatedFeatures.forEach((feature) => {
                  if (feature.is_static) {
                    // Update static Package fields
                    if (feature.key === 'storage_mo') newFormData.storage_mo = feature.value;
                    else if (feature.key === 'total_user') newFormData.total_user = feature.value;
                    else if (feature.key === 'monthly_limit_transaction_ai') newFormData.monthly_limit_transaction_ai = feature.value;
                    else if (feature.key === 'total_transaction_ai') newFormData.total_transaction_ai = feature.value;
                    else if (feature.key === 'max_upload') newFormData.max_upload = feature.value;
                    else if (feature.key === 'max_jobs') newFormData.max_jobs = feature.value;
                    else if (feature.key === 'data_retention') newFormData.data_retention = feature.value;
                  } else {
                    // Update dynamic features
                    newPackageFeatures[feature.id] = feature.value;
                  }
                });
                
                setFormData(newFormData);
                setPackageFeatures(newPackageFeatures);
              }}
            />

          </div>


          {/* Action Buttons */}
          <div className="flex justify-end gap-3 rounded-xl bg-white p-6 shadow-md dark:bg-navy-800">
            <button
              type="button"
              onClick={() => navigate('/settings/packages')}
              className="rounded-lg border border-gray-300 px-6 py-2.5 font-medium text-gray-700 transition hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2.5 font-medium text-white shadow-lg transition hover:bg-brand-600 disabled:opacity-50"
            >
              <FiSave className="h-5 w-5" />
              {loading ? 'Updating...' : 'Update Package'}
            </button>
          </div>
        </form>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Feature Management Modal */}
      <Modal
        isOpen={showFeatureModal}
        onClose={() => setShowFeatureModal(false)}
        title={editingFeature ? '✏️ Edit Feature' : ' Add New Feature'}
        size="lg"
      >
        <form onSubmit={handleFeatureSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              🔑 Feature Key (Unique Identifier) *
            </label>
            <input
              type="text"
              value={featureFormData.key}
              onChange={(e) => setFeatureFormData({ ...featureFormData, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
              placeholder="e.g., advanced_analytics"
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              required
              disabled={!!editingFeature}
            />
            <p className="mt-1 text-xs text-gray-500">
              {editingFeature ? 'Key cannot be changed after creation' : 'Use lowercase letters, numbers, and underscores only. E.g., max_users, api_access'}
            </p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              🇬🇧 Feature Title (English) *
            </label>
            <input
              type="text"
              value={featureFormData.title_en}
              onChange={(e) => setFeatureFormData({ ...featureFormData, title_en: e.target.value })}
              placeholder="e.g., Advanced Analytics"
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              🇫🇷 Feature Title (French)
            </label>
            <input
              type="text"
              value={featureFormData.title_fr}
              onChange={(e) => setFeatureFormData({ ...featureFormData, title_fr: e.target.value })}
              placeholder="e.g., Analytique avancée"
              className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              🔢 Value Type *
            </label>
            <select
              value={featureFormData.value_type}
              onChange={(e) => setFeatureFormData({ ...featureFormData, value_type: e.target.value as 'boolean' | 'integer' | 'text' })}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            >
              <option value="boolean">Boolean (Yes/No Toggle)</option>
              <option value="integer">Integer (Number)</option>
              <option value="text">Text (String)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Boolean: On/Off switch | Integer: Numeric value | Text: Text input
            </p>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="feature_is_active"
              checked={featureFormData.is_active}
              onChange={(e) => setFeatureFormData({ ...featureFormData, is_active: e.target.checked })}
              className="h-5 w-5 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
            />
            <label htmlFor="feature_is_active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              ✓ Feature is Active
            </label>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="feature_is_new"
              checked={featureFormData.is_new}
              onChange={(e) => setFeatureFormData({ ...featureFormData, is_new: e.target.checked })}
              className="h-5 w-5 rounded border-gray-300 text-green-500 focus:ring-green-500"
            />
            <label htmlFor="feature_is_new" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              🆕 Show NEW Badge
            </label>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-navy-600">
            <button
              type="button"
              onClick={() => setShowFeatureModal(false)}
              className="rounded-lg border border-gray-300 px-6 py-2 font-medium text-gray-700 transition hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!featureFormData.key || !featureFormData.title_en}
              className="rounded-lg bg-brand-500 px-6 py-2 font-medium text-white transition hover:bg-brand-600 disabled:opacity-50"
            >
              {editingFeature ? 'Update Feature' : 'Create Feature'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Feature Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmDialog.isOpen}
        title="⚠️ Delete Feature"
        message={`Are you sure you want to permanently delete the feature "${deleteConfirmDialog.featureName}"?\n\nThis feature will be REMOVED from ALL packages!\n\nThis action cannot be undone.`}
        type="danger"
        confirmText="Delete Feature"
        cancelText="Cancel"
        onConfirm={confirmDeleteFeature}
        onClose={() => setDeleteConfirmDialog({ isOpen: false, featureId: null, featureName: '' })}
      />
    </Layout>
  );
};

export default EditPackage;
